import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  getExchangeRates,
  deleteExchangeRate,
  clearError,
  clearSuccess,
} from '../../../../redux/Slices/authSlice';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaPrint,
  FaFileExcel,
  FaSearch,
  FaFilter,
  FaSort,
  FaSortUp,
  FaSortDown,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/i18n';
import { convertArabicNumbersToEnglish } from '../../../../shared/utils.js/utils';
import usePdfExport from '../../../../shared/pdf/usePdfExport';
import PdfExportManager from '../../../../shared/pdf/PdfExportManager';
import { exportRowsToExcel } from '../../../../shared/utils.js/excelExport';

const SEARCH_DEBOUNCE_MS = 400;
const SORT_FIELDS = {
  index: 'index',
  date: 'date',
  rate: 'rate',
  currency: 'currency',
  currencyCode: 'currencyCode',
};

const ExchangeRates = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setTitle } = useTitle();

  const { isLoading, error, success } = useSelector(state => state.auth);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [perPageInput, setPerPageInput] = useState(10);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [rateToDelete, setRateToDelete] = useState(null);
  const [allExchangeRates, setAllExchangeRates] = useState([]);

  const lastErrorRef = useRef({ message: null, time: 0 });
  const lastSuccessRef = useRef({ message: null, time: 0 });

  const { t } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';

  useEffect(() => {
    setTitle('sidenav.exchangeRates');
  }, [setTitle]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchExchangeRates = useCallback(async () => {
    try {
      const result = await dispatch(getExchangeRates({ page: 1, per_page: 10 }));
      if (!getExchangeRates.fulfilled.match(result)) return;
      const response = result.payload;
      const data = response?.data || response;
      const meta = data?.meta || data?.pagination || null;
      const firstList = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data)
          ? data
          : [];
      const total = meta?.total || firstList.length;

      if (total <= firstList.length) {
        setAllExchangeRates(firstList);
        return;
      }
      const allResult = await dispatch(
        getExchangeRates({ page: 1, per_page: total }),
      );
      if (getExchangeRates.fulfilled.match(allResult)) {
        const allData = allResult.payload?.data || allResult.payload;
        setAllExchangeRates(
          Array.isArray(allData?.data)
            ? allData.data
            : Array.isArray(allData)
              ? allData
              : [],
        );
      }
    } catch {
      /* errors surface via the auth slice */
    }
  }, [dispatch]);

  useEffect(() => {
    fetchExchangeRates();
    dispatch(clearSuccess());
    dispatch(clearError());
  }, [fetchExchangeRates, dispatch, i18n.language]);

  useEffect(() => {
    if (success) {
      const now = Date.now();
      const last = lastSuccessRef.current;
      if (!last.message || last.message !== success || now - last.time > 2000) {
        toast.success(success, { rtl: isRTL, autoClose: 3000 });
        lastSuccessRef.current = { message: success, time: now };
      }
      dispatch(clearSuccess());
      fetchExchangeRates();
    }
  }, [success, dispatch, isRTL, fetchExchangeRates]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL, autoClose: 5000 });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [error, dispatch, isRTL]);

  const formatDate = dateString => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const getCurrencyName = rate =>
    isRTL
      ? rate.currency?.name_ar || rate.currency?.name || ''
      : rate.currency?.name_en || rate.currency?.name || '';

  const filteredExchangeRates = useMemo(() => {
    let result = allExchangeRates || [];

    if (debouncedSearch) {
      const term = convertArabicNumbersToEnglish(debouncedSearch)
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
      result = result.filter(r => {
        const check = v =>
          convertArabicNumbersToEnglish(String(v || ''))
            .toLowerCase()
            .includes(term);
        return (
          check(r.rate) ||
          check(r.currency?.code) ||
          check(r.currency?.name_ar) ||
          check(r.currency?.name_en) ||
          check(r.currency?.name) ||
          check(formatDate(r.date))
        );
      });
    }

    if (dateFrom) result = result.filter(r => r.date && r.date >= dateFrom);
    if (dateTo) result = result.filter(r => r.date && r.date <= dateTo);

    if (sortField) {
      result = [...result].sort((a, b) => {
        let aVal;
        let bVal;
        if (sortField === SORT_FIELDS.index) {
          aVal = a.id ?? 0;
          bVal = b.id ?? 0;
        } else if (sortField === SORT_FIELDS.date) {
          aVal = a.date ?? '';
          bVal = b.date ?? '';
        } else if (sortField === SORT_FIELDS.rate) {
          aVal = Number(a.rate ?? 0);
          bVal = Number(b.rate ?? 0);
        } else if (sortField === SORT_FIELDS.currency) {
          aVal = getCurrencyName(a).toLowerCase();
          bVal = getCurrencyName(b).toLowerCase();
        } else if (sortField === SORT_FIELDS.currencyCode) {
          aVal = (a.currency?.code || '').toLowerCase();
          bVal = (b.currency?.code || '').toLowerCase();
        }
        if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allExchangeRates, debouncedSearch, dateFrom, dateTo, sortField, sortOrder, isRTL]);

  const totalPages = Math.max(1, Math.ceil(filteredExchangeRates.length / perPage));
  const currentPageSafe = Math.min(currentPage, totalPages);
  const paginatedExchangeRates = filteredExchangeRates.slice(
    (currentPageSafe - 1) * perPage,
    currentPageSafe * perPage,
  );

  const handleApplyPerPage = () => {
    const v = Math.max(
      1,
      Math.min(perPageInput, filteredExchangeRates.length || 1000),
    );
    setPerPage(v);
    setCurrentPage(1);
  };

  const handleSort = field => {
    if (sortField === field) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const getSortIcon = field => {
    if (sortField !== field)
      return <FaSort style={{ opacity: 0.4, marginInlineStart: '5px' }} />;
    return sortOrder === 'asc' ? (
      <FaSortUp style={{ color: '#f0b429', marginInlineStart: '5px' }} />
    ) : (
      <FaSortDown style={{ color: '#f0b429', marginInlineStart: '5px' }} />
    );
  };

  const applyFilters = () => setCurrentPage(1);

  const resetFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setDateFrom('');
    setDateTo('');
    setSortField('');
    setSortOrder('asc');
    setCurrentPage(1);
  };

  const hasActiveFilters = Boolean(debouncedSearch || dateFrom || dateTo);

  const handleDelete = rate => {
    setRateToDelete(rate);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (rateToDelete) {
      await dispatch(deleteExchangeRate(rateToDelete.id));
      setShowDeleteModal(false);
      setRateToDelete(null);
      fetchExchangeRates();
    }
  };

  // ─── Shared PDF / Excel column config ──────────────────────────────────
  const exportColumns = useMemo(
    () => [
      {
        key: 'date',
        label: t('exchangeRates.table.date', { defaultValue: 'Date' }),
        render: rate => formatDate(rate.date),
        excelValue: rate => formatDate(rate.date),
      },
      {
        key: 'rate',
        label: t('exchangeRates.table.rate', { defaultValue: 'Rate' }),
      },
      {
        key: 'currency',
        label: t('exchangeRates.table.currency', { defaultValue: 'Currency' }),
        align: 'start',
        render: rate => getCurrencyName(rate) || '—',
        excelValue: rate => getCurrencyName(rate) || '—',
      },
      {
        key: 'currencyCode',
        label: t('exchangeRates.table.currencyCode', {
          defaultValue: 'Currency Code',
        }),
        render: rate => rate.currency?.code || '—',
        excelValue: rate => rate.currency?.code || '—',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, isRTL],
  );

  const pdf = usePdfExport({
    idPrefix: 'exchange-rates',
    isRTL,
    fileNamePrefix: 'ACE_ExchangeRates',
    onError: err =>
      toast.error(
        err?.message === 'NO_DATA'
          ? t('exchangeRates.print.noData', {
              defaultValue: 'No data available for printing',
            })
          : t('exchangeRates.print.error', {
              defaultValue: 'An error occurred while creating the PDF',
            }),
        { rtl: isRTL },
      ),
  });

  const handleExportExchangeRateToExcel = rate => {
    exportRowsToExcel({
      columns: exportColumns,
      rows: [rate],
      fileName: `${t('exchangeRates.excel.fileName', { defaultValue: 'ExchangeRate' })}_${rate.id}`,
      sheetName: t('exchangeRates.excel.sheetName', {
        defaultValue: 'Exchange Rate',
      }),
      rtl: isRTL,
    });
    toast.success(
      t('exchangeRates.export.success', {
        defaultValue: 'Data exported successfully',
      }),
      { rtl: isRTL },
    );
  };

  const handleExportAllToExcel = () => {
    if (!filteredExchangeRates || filteredExchangeRates.length === 0) {
      toast.error(
        t('exchangeRates.export.noData', { defaultValue: 'No data to export' }),
        { rtl: isRTL },
      );
      return;
    }
    exportRowsToExcel({
      columns: exportColumns,
      rows: filteredExchangeRates,
      fileName: t('exchangeRates.excel.fileName', {
        defaultValue: 'ExchangeRates',
      }),
      sheetName: t('exchangeRates.excel.sheetName', {
        defaultValue: 'Exchange Rates',
      }),
      rtl: isRTL,
    });
    toast.success(
      t('exchangeRates.export.success', {
        defaultValue: 'Data exported successfully',
      }),
      { rtl: isRTL },
    );
  };

  // Applied-filters summary shown in the PDF header
  const filtersSummary = useMemo(() => {
    const parts = [];
    if (debouncedSearch)
      parts.push(
        `${t('exchangeRates.search.label', { defaultValue: 'بحث' })}: ${debouncedSearch}`,
      );
    if (dateFrom)
      parts.push(
        `${t('exchangeRates.filter.dateFrom', { defaultValue: 'Date From' })}: ${dateFrom}`,
      );
    if (dateTo)
      parts.push(
        `${t('exchangeRates.filter.dateTo', { defaultValue: 'Date To' })}: ${dateTo}`,
      );
    return parts.join(' | ') || undefined;
  }, [debouncedSearch, dateFrom, dateTo, t]);

  // Single-record PDF content — key/value grid (same layout as before)
  const renderSingleRate = rate => (
    <div style={{ border: '1px solid #000', padding: '12px', marginBottom: '12px' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '10px',
          fontSize: '13px',
          lineHeight: 1.6,
          direction: dir,
          textAlign,
        }}
      >
        <div>
          <strong>{t('exchangeRates.table.date', { defaultValue: 'Date' })}:</strong>{' '}
          {formatDate(rate.date)}
        </div>
        <div>
          <strong>{t('exchangeRates.table.rate', { defaultValue: 'Rate' })}:</strong>{' '}
          {rate.rate || '—'}
        </div>
        <div>
          <strong>
            {t('exchangeRates.table.currency', { defaultValue: 'Currency' })}:
          </strong>{' '}
          {getCurrencyName(rate) || '—'}
        </div>
        <div>
          <strong>
            {t('exchangeRates.table.currencyCode', {
              defaultValue: 'Currency Code',
            })}
            :
          </strong>{' '}
          {rate.currency?.code || '—'}
        </div>
      </div>
    </div>
  );

  if (isLoading && (!allExchangeRates || allExchangeRates.length === 0)) {
    return (
      <div
        style={{
          padding: '30px',
          backgroundColor: '#14263b',
          minHeight: 'calc(100vh - 80px)',
          color: 'white',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          direction: dir,
        }}
      >
        {t('common.loading', { defaultValue: 'Loading...' })}
      </div>
    );
  }

  const noData = filteredExchangeRates.length === 0;

  return (
    <>
      <div
        style={{
          padding: '30px',
          backgroundColor: '#14263b',
          minHeight: 'calc(100vh - 80px)',
          color: 'white',
          direction: dir,
        }}
      >
        {/* ── Top Bar ── */}
        <div style={{ marginBottom: '30px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              flexWrap: 'wrap',
              gap: '15px',
            }}
          >
            <h1
              style={{
                fontSize: '24px',
                fontWeight: 'bold',
                color: 'white',
                margin: 0,
                textAlign,
              }}
            >
              {t('exchangeRates.title', { defaultValue: 'Exchange Rates' })}
            </h1>

            <div
              style={{
                display: 'flex',
                gap: '10px',
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              {/* Per-page */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexWrap: 'wrap',
                }}
              >
                <label
                  style={{ color: 'white', fontSize: '14px', whiteSpace: 'nowrap' }}
                >
                  {t('common.perPage', { defaultValue: 'عدد السجلات' })}:
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <button
                    onClick={() => setPerPageInput(Math.max(1, perPageInput - 1))}
                    style={{
                      backgroundColor: '#1b3350',
                      color: 'white',
                      border: '1px solid #333',
                      padding: '6px 10px',
                      borderRadius: '6px 0 0 6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      minWidth: '32px',
                    }}
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min="1"
                    max="1000"
                    value={perPageInput}
                    onChange={e =>
                      setPerPageInput(
                        Math.max(1, Math.min(parseInt(e.target.value, 10) || 1, 1000)),
                      )
                    }
                    style={{
                      width: '70px',
                      padding: '6px 10px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderLeft: 'none',
                      borderRight: 'none',
                      borderRadius: 0,
                      color: 'white',
                      fontSize: '14px',
                      textAlign: 'center',
                    }}
                  />
                  <button
                    onClick={() => setPerPageInput(Math.min(1000, perPageInput + 1))}
                    style={{
                      backgroundColor: '#1b3350',
                      color: 'white',
                      border: '1px solid #333',
                      padding: '6px 10px',
                      borderRadius: '0 6px 6px 0',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      minWidth: '32px',
                    }}
                  >
                    +
                  </button>
                </div>
                <button
                  onClick={handleApplyPerPage}
                  style={{
                    backgroundColor: '#AC2000',
                    color: 'white',
                    border: 'none',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('common.apply', { defaultValue: 'تطبيق' })}
                </button>
              </div>

              <button
                onClick={() => pdf.previewAll(filteredExchangeRates)}
                disabled={noData}
                style={{
                  backgroundColor: noData ? '#666' : '#444',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  cursor: noData ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexDirection: isRTL ? 'row' : 'row-reverse',
                }}
              >
                <FaEye />{' '}
                {t('exchangeRates.preview.all', { defaultValue: 'Preview PDF All' })}
              </button>

              <button
                onClick={() => pdf.printAll(filteredExchangeRates)}
                disabled={pdf.isGeneratingPdf || noData}
                style={{
                  backgroundColor: pdf.isGeneratingPdf || noData ? '#666' : '#1f5ff1',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  cursor: pdf.isGeneratingPdf || noData ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexDirection: isRTL ? 'row' : 'row-reverse',
                  opacity: pdf.isGeneratingPdf ? 0.6 : 1,
                }}
              >
                <FaPrint />{' '}
                {pdf.isGeneratingPdf
                  ? t('exchangeRates.print.generating', {
                      defaultValue: 'Generating...',
                    })
                  : t('exchangeRates.print.all', { defaultValue: 'Print PDF All' })}
              </button>

              <button
                onClick={handleExportAllToExcel}
                disabled={noData}
                style={{
                  backgroundColor: noData ? '#666' : '#27ae60',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  cursor: noData ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexDirection: isRTL ? 'row' : 'row-reverse',
                }}
              >
                <FaFileExcel />{' '}
                {t('exchangeRates.export.all', { defaultValue: 'Export Excel' })}
              </button>

              <button
                onClick={() => navigate('/exchange-rates/add')}
                style={{
                  backgroundColor: '#AC2000',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexDirection: isRTL ? 'row' : 'row-reverse',
                }}
              >
                <FaPlus />{' '}
                {t('exchangeRates.add', { defaultValue: 'Add Exchange Rate' })}
              </button>
            </div>
          </div>

          {/* ── Search + Filter toggle ── */}
          <div
            style={{
              display: 'flex',
              gap: '10px',
              alignItems: 'center',
              flexWrap: 'wrap',
              marginBottom: '12px',
            }}
          >
            <div style={{ position: 'relative', flex: 1, minWidth: '220px' }}>
              <FaSearch
                style={{
                  position: 'absolute',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  [isRTL ? 'right' : 'left']: '12px',
                  color: '#888',
                  fontSize: '14px',
                  pointerEvents: 'none',
                }}
              />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t('exchangeRates.search.placeholder', {
                  defaultValue: 'ابحث بالعملة أو الكود أو التاريخ...',
                })}
                aria-label={t('exchangeRates.search.placeholder', {
                  defaultValue: 'Search',
                })}
                style={{
                  width: '100%',
                  paddingInlineStart: '40px',
                  paddingInlineEnd: search ? '32px' : '12px',
                  paddingTop: '10px',
                  paddingBottom: '10px',
                  backgroundColor: '#1b3350',
                  color: 'white',
                  border: '1px solid #444',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box',
                  direction: dir,
                  textAlign,
                }}
              />
              {search && (
                <button
                  onClick={resetFilters}
                  aria-label={t('common.reset', { defaultValue: 'Reset' })}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    [isRTL ? 'left' : 'right']: '10px',
                    background: 'none',
                    border: 'none',
                    color: '#aaa',
                    cursor: 'pointer',
                    fontSize: '16px',
                    lineHeight: 1,
                    padding: 0,
                  }}
                >
                  ×
                </button>
              )}
            </div>

            <button
              onClick={() => setShowFilters(prev => !prev)}
              style={{
                backgroundColor: showFilters ? '#AC2000' : '#1b3350',
                color: 'white',
                border: '1px solid #444',
                padding: '10px 16px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '7px',
                flexDirection: isRTL ? 'row' : 'row-reverse',
                whiteSpace: 'nowrap',
              }}
            >
              <FaFilter />
              {t('common.filters', { defaultValue: 'Filters' })}
              {hasActiveFilters && (
                <span
                  style={{
                    backgroundColor: '#f0b429',
                    color: '#000',
                    borderRadius: '50%',
                    width: '18px',
                    height: '18px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '11px',
                    fontWeight: 'bold',
                  }}
                >
                  !
                </span>
              )}
            </button>
          </div>

          {/* ── Filter Panel ── */}
          {showFilters && (
            <div
              style={{
                backgroundColor: '#1b3350',
                border: '1px solid #444',
                borderRadius: '10px',
                padding: '16px',
                marginBottom: '12px',
                display: 'flex',
                gap: '16px',
                flexWrap: 'wrap',
                alignItems: 'flex-end',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                  minWidth: '180px',
                }}
              >
                <label style={{ color: '#ccc', fontSize: '13px' }}>
                  {t('exchangeRates.filter.dateFrom', { defaultValue: 'Date From' })}
                </label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={e => {
                    setDateFrom(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{
                    backgroundColor: '#1a1f2e',
                    color: 'white',
                    border: '1px solid #555',
                    borderRadius: '6px',
                    padding: '8px 10px',
                    fontSize: '13px',
                  }}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                  minWidth: '180px',
                }}
              >
                <label style={{ color: '#ccc', fontSize: '13px' }}>
                  {t('exchangeRates.filter.dateTo', { defaultValue: 'Date To' })}
                </label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={e => {
                    setDateTo(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{
                    backgroundColor: '#1a1f2e',
                    color: 'white',
                    border: '1px solid #555',
                    borderRadius: '6px',
                    padding: '8px 10px',
                    fontSize: '13px',
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  onClick={applyFilters}
                  style={{
                    backgroundColor: '#AC2000',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 'bold',
                  }}
                >
                  {t('common.apply', { defaultValue: 'Apply' })}
                </button>
                <button
                  onClick={resetFilters}
                  style={{
                    backgroundColor: '#333',
                    color: 'white',
                    border: '1px solid #555',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 'bold',
                  }}
                >
                  {t('common.reset', { defaultValue: 'Reset' })}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Table ── */}
        <div
          style={{
            backgroundColor: '#1b3350',
            borderRadius: '12px',
            border: '1px solid #333',
            overflow: 'hidden',
          }}
        >
          {paginatedExchangeRates && paginatedExchangeRates.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr
                    style={{
                      backgroundColor: '#1a1f2e',
                      borderBottom: '2px solid #AC2000',
                    }}
                  >
                    <th onClick={() => handleSort(SORT_FIELDS.index)} style={thSortable}>
                      {t('common.serialNumber', { defaultValue: '#' })}{' '}
                      {getSortIcon(SORT_FIELDS.index)}
                    </th>
                    <th onClick={() => handleSort(SORT_FIELDS.date)} style={thSortable}>
                      {t('exchangeRates.table.date', { defaultValue: 'Date' })}{' '}
                      {getSortIcon(SORT_FIELDS.date)}
                    </th>
                    <th onClick={() => handleSort(SORT_FIELDS.rate)} style={thSortable}>
                      {t('exchangeRates.table.rate', { defaultValue: 'Rate' })}{' '}
                      {getSortIcon(SORT_FIELDS.rate)}
                    </th>
                    <th
                      onClick={() => handleSort(SORT_FIELDS.currency)}
                      style={thSortable}
                    >
                      {t('exchangeRates.table.currency', { defaultValue: 'Currency' })}{' '}
                      {getSortIcon(SORT_FIELDS.currency)}
                    </th>
                    <th
                      onClick={() => handleSort(SORT_FIELDS.currencyCode)}
                      style={thSortable}
                    >
                      {t('exchangeRates.table.currencyCode', {
                        defaultValue: 'Currency Code',
                      })}{' '}
                      {getSortIcon(SORT_FIELDS.currencyCode)}
                    </th>
                    <th style={{ ...thSortable, cursor: 'default' }}>
                      {t('exchangeRates.table.actions', { defaultValue: 'Actions' })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedExchangeRates.map((rate, index) => {
                    const recordIndex = (currentPageSafe - 1) * perPage + index + 1;
                    return (
                      <tr
                        key={rate.id}
                        style={{
                          borderBottom: '2px solid #444',
                          borderTop: index === 0 ? '2px solid #444' : 'none',
                        }}
                      >
                        <td style={tdCenter}>{recordIndex}</td>
                        <td style={tdCenter}>{formatDate(rate.date)}</td>
                        <td style={tdCenter}>{rate.rate || '—'}</td>
                        <td
                          style={{
                            padding: '15px',
                            textAlign,
                            color: 'white',
                            direction: dir,
                            verticalAlign: 'middle',
                          }}
                        >
                          {getCurrencyName(rate) || '—'}
                        </td>
                        <td style={tdCenter}>{rate.currency?.code || '—'}</td>
                        <td style={{ padding: '15px' }}>
                          <div
                            style={{
                              display: 'flex',
                              gap: '8px',
                              justifyContent: 'center',
                            }}
                          >
                            <IconButton
                              bg="#666"
                              onClick={() => pdf.previewSingle(rate)}
                              title={t('exchangeRates.actions.view', {
                                defaultValue: 'View Details',
                              })}
                            >
                              <FaEye />
                            </IconButton>
                            <IconButton
                              bg="#007bff"
                              onClick={() => navigate(`/exchange-rates/${rate.id}`)}
                              title={t('exchangeRates.actions.edit', {
                                defaultValue: 'Edit',
                              })}
                            >
                              <FaEdit />
                            </IconButton>
                            <IconButton
                              bg="#1f5ff1"
                              onClick={() => pdf.printSingle(rate, rate.id)}
                              disabled={pdf.isGeneratingPdf}
                              title={t('exchangeRates.actions.print', {
                                defaultValue: 'Print',
                              })}
                            >
                              <FaPrint />
                            </IconButton>
                            <IconButton
                              bg="#28a745"
                              onClick={() => handleExportExchangeRateToExcel(rate)}
                              title={t('exchangeRates.actions.export', {
                                defaultValue: 'Export Excel',
                              })}
                            >
                              <FaFileExcel />
                            </IconButton>
                            <IconButton
                              bg="#c0392b"
                              onClick={() => handleDelete(rate)}
                              title={t('exchangeRates.actions.delete', {
                                defaultValue: 'Delete',
                              })}
                            >
                              <FaTrash />
                            </IconButton>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              {t('exchangeRates.noData', { defaultValue: 'No exchange rates found' })}
            </div>
          )}

          {/* ── Pagination ── */}
          {totalPages > 1 &&
            (() => {
              const pages = [];
              for (let i = 1; i <= totalPages; i += 1) {
                if (
                  i === 1 ||
                  i === totalPages ||
                  (i >= currentPageSafe - 2 && i <= currentPageSafe + 2)
                )
                  pages.push(i);
                else if (i === currentPageSafe - 3 || i === currentPageSafe + 3)
                  pages.push('...');
              }
              return (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '8px',
                    marginTop: '20px',
                    paddingBottom: '20px',
                    borderTop: '1px solid #333',
                    paddingTop: '20px',
                  }}
                >
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPageSafe - 1))}
                    disabled={currentPageSafe === 1}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: currentPageSafe === 1 ? '#333' : '#AC2000',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: currentPageSafe === 1 ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {t('common.previous', { defaultValue: 'Previous' })}
                  </button>
                  {pages.map((p, idx) =>
                    p === '...' ? (
                      <span
                        key={`ellipsis-${idx}`}
                        style={{ padding: '8px', color: 'white' }}
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setCurrentPage(p)}
                        style={{
                          padding: '8px 12px',
                          backgroundColor:
                            p === currentPageSafe ? '#AC2000' : '#1a1f2e',
                          color: 'white',
                          border: '1px solid #333',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                      >
                        {p}
                      </button>
                    ),
                  )}
                  <button
                    onClick={() =>
                      setCurrentPage(Math.min(totalPages, currentPageSafe + 1))
                    }
                    disabled={currentPageSafe === totalPages}
                    style={{
                      padding: '8px 12px',
                      backgroundColor:
                        currentPageSafe === totalPages ? '#333' : '#AC2000',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor:
                        currentPageSafe === totalPages ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {t('common.next', { defaultValue: 'Next' })}
                  </button>
                </div>
              );
            })()}
        </div>

        {/* ── Delete Modal ── */}
        {showDeleteModal && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.7)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 999999,
            }}
          >
            <div
              style={{
                backgroundColor: '#1a1f2e',
                padding: '30px',
                borderRadius: '8px',
                width: '90%',
                maxWidth: '400px',
                border: '1px solid #333',
                direction: dir,
              }}
            >
              <h2
                style={{
                  color: 'white',
                  marginBottom: '20px',
                  fontSize: '20px',
                  textAlign,
                }}
              >
                {t('exchangeRates.delete.title', { defaultValue: 'Confirm Delete' })}
              </h2>
              <p style={{ color: '#ccc', marginBottom: '30px', textAlign }}>
                {t('exchangeRates.delete.confirm', {
                  defaultValue: 'Are you sure you want to delete this exchange rate?',
                })}
              </p>
              <p
                style={{
                  color: '#dc3545',
                  fontSize: '13px',
                  marginBottom: '20px',
                  textAlign,
                }}
              >
                {t('exchangeRates.delete.irreversible', {
                  defaultValue: 'This action cannot be undone.',
                })}
              </p>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '12px',
                  flexDirection: isRTL ? 'row' : 'row-reverse',
                }}
              >
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setRateToDelete(null);
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#333',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  {t('exchangeRates.delete.cancel', { defaultValue: 'Cancel' })}
                </button>
                <button
                  onClick={confirmDelete}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  {t('exchangeRates.delete.confirmButton', { defaultValue: 'Delete' })}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Shared PDF Engine — measurement, preview, print & single-record */}
      <PdfExportManager
        pdf={pdf}
        title={t('exchangeRates.title', { defaultValue: 'Exchange Rates' })}
        columns={exportColumns}
        rows={filteredExchangeRates}
        filtersSummary={filtersSummary}
        singleTitle={t('exchangeRates.print.title', {
          defaultValue: 'Exchange Rate Details',
        })}
        renderSingle={renderSingleRate}
      />
    </>
  );
};

const thSortable = {
  padding: '15px',
  textAlign: 'center',
  color: 'white',
  fontWeight: 'bold',
  fontSize: '16px',
  borderBottom: '1px solid #333',
  cursor: 'pointer',
  userSelect: 'none',
  whiteSpace: 'nowrap',
};

const tdCenter = {
  padding: '15px',
  textAlign: 'center',
  color: 'white',
  verticalAlign: 'middle',
};

const IconButton = ({ children, onClick, title, bg, disabled = false }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={title}
    aria-label={title}
    style={{
      backgroundColor: bg,
      color: 'white',
      border: 'none',
      padding: '8px',
      borderRadius: '6px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize: '14px',
      opacity: disabled ? 0.7 : 1,
    }}
  >
    {children}
  </button>
);

export default ExchangeRates;
